import { Component } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { WorksDialogComponent } from 'src/app/pages/works-dialog/works-dialog.component'
import { AppSettingsService } from 'src/app/settings/services/app-settings.service'
import { BuilderService } from '../../builder-services/builder.service'
import { IosbuildService } from './iosbuild.service'
import { Observable } from 'rxjs'
import { finalize, map } from 'rxjs/operators'

@Component({
  selector: 'ios-build',
  templateUrl: './ios-build.component.html',
  styleUrls: ['./ios-build.component.scss']
})
export class IosBuildComponent {
  // ─── IOS Build State ──────────────────────────────────────────
  buildStart = false
  value = 0
  qrLink = ''
  downloadBtn = ''
  downloadAABBtn = ''
  checkingStatus = true
  error = false
  buildErrorMsg = ''
  appleSettingsData: any = {}
  saving = false
  appleSettingsFields = ['key_id', 'issuer_id', 'key_filepath', 'email', 'app_specific_pass', 'team_name', 'team_id']
  iosBuildForm = this.formBuilder.group({
    iosVersion: ''
  })
  appleSettingsForm = this.formBuilder.group({
    key_id: '',
    issuer_id: '',
    key_filepath: '',
    itunes_password: '',
    email: '',
    app_specific_pass: '',
    team_name: '',
    team_id: ''
  })
  constructor(
    private formBuilder: FormBuilder,
    public builderService: BuilderService,
    private appSettingsService: AppSettingsService,
    private iosService: IosbuildService,
    public dialog: MatDialog
  ) {
    // Load Apple Settings and prefill form
    this.appSettingsService.getAppleSettings(this.builderService.getSelectedProject()).subscribe(
      (settings: any) => {
        this.appleSettingsData = settings || {}
        this.appleSettingsForm.patchValue(this.appleSettingsData)
      },
      (err) => {
        console.log(err)
      }
    )
    this.getBuildStatus()
  }

  /**
   * Submits the IOS build request to the server
   */
  onSubmit(): void {
    this.value = 0
    this.buildStart = true
    this.builderService.headerBtn = ''
    this.error = false
    if (!this.iosBuildForm.value.iosVersion) {
      this.iosBuildForm.value.iosVersion = '0.1'
    }
    this.iosService.startBuild(this.builderService.getSelectedProject(), this.iosBuildForm.value.iosVersion).subscribe((response: any) => {
      this.getBuildStatus()
    })
  }

  saveSettings(): void {
    this.saveAppleSettings().subscribe()
  }

  /**
   * Returns true only when all required Apple Settings fields are filled
   */
  get appleSettingsFilled(): boolean {
    const v = this.appleSettingsForm.value
    const d = this.appleSettingsData
    return !!(
      (v.key_id || d.key_id) &&
      (v.issuer_id || d.issuer_id) &&
      (v.team_name || d.team_name) &&
      (v.team_id || d.team_id) &&
      (v.email || d.email) &&
      (v.itunes_password || d.itunes_password) &&
      (v.app_specific_pass || d.app_specific_pass) &&
      (v.key_filepath || d.key_filepath) // <-- accept either form value OR uploaded path
    )
  }

  /**
   * Saves the Apple Settings form data
   */
  saveAppleSettings(): Observable<any> {
    this.saving = true

    let key_filepath = ''
    if (this.appleSettingsData && this.appleSettingsData.key_filepath) {
      key_filepath = this.appleSettingsData.key_filepath
    } else if (this.appleSettingsForm.value.key_filepath) {
      key_filepath = this.appleSettingsForm.value.key_filepath
    }

    if (key_filepath) {
      key_filepath = decodeURI(key_filepath)
    }

    let newSubmittedData: any = {}
    let submittedData: any = this.appleSettingsForm.value
    let retrievedData: any = this.appleSettingsData ? this.appleSettingsData : {}

    for (var k in submittedData) {
      if (submittedData[k] != '') {
        newSubmittedData[k] = submittedData[k]
      } else {
        newSubmittedData[k] = retrievedData[k]
      }
    }

    // Ensure uploaded filepath is set correctly
    newSubmittedData['key_filepath'] = key_filepath

    return this.appSettingsService.setAppleSettings(this.builderService.selectedProject, newSubmittedData).pipe(
      map((response: any) => {
        // optional: keep local data synced so UI stays consistent
        if (typeof this.appleSettingsData !== 'object' || this.appleSettingsData === null) {
          this.appleSettingsData = {}
        }
        for (var key in newSubmittedData) {
          this.appleSettingsData[key] = newSubmittedData[key]
        }
        return response
      }),
      finalize(() => {
        this.saving = false
      })
    )
  }

  /**
   * Gets the build status from the builder and brain, incase the app is still progressing then we will rerun the same function checker again after 5 seconds
   */
  getBuildStatus = () => {
    this.iosService.getProgress(this.builderService.getSelectedProject(), this.iosBuildForm.value.iosVersion).subscribe((progressRes: any) => {
      this.value = progressRes.data
      this.checkingStatus = false
      if (this.value === 0 && progressRes.status !== 'error') {
        this.buildStart = false
      } else {
        if (this.value <= 100) {
          if (progressRes.status !== 'error' && this.value == 100) {
            this.downloadBtn = progressRes.ios_url
            this.downloadAABBtn = progressRes.aab_url
            this.qrLink = 'https://qrcode.tec-it.com/API/QRCode?data=' + progressRes.ios_url
            this.error = false
          } else if (progressRes.status == 'error') {
            this.error = true
            this.builderService.headerBtn == 'ios'
            this.getBuildError()
          } else {
            setTimeout(() => {
              this.getBuildStatus()
            }, 5000)
            this.error = false
          }
          this.buildStart = true
        }
      }
    })
  }

  getBuildError = () => {
    this.iosService.getBuildError(this.builderService.getSelectedProject()).subscribe((response: any) => {
      this.buildErrorMsg = decodeURIComponent(response.message.replace(/\+/g, ' '))
    })
  }

  rebuildApp = () => {
    this.buildStart = false
    this.checkingStatus = false
    this.error = false
  }

  howItWorksDialog = () => {
    const dialogRef = this.dialog.open(WorksDialogComponent, {
      width: '700px',
      maxWidth: '80%',
      data: { name: 'ios' }
    })

    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed')
    })
  }

  /**
   * Handles p8 file upload, reads and uploads the file to the server
   */
  processFile(fileInput: any, type: string, name: string) {
    const file: File = fileInput.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.addEventListener('load', (event: any) => {
      this.builderService.showUpdateProgress = true
      this.appSettingsService.uploadFile(file, this.builderService.selectedProject, type).subscribe(
        (res) => {
          if (typeof this.appleSettingsData !== 'object' || this.appleSettingsData === null) {
            this.appleSettingsData = {}
          }
          this.appleSettingsData[name] = res
          this.builderService.showUpdateProgress = false
        },
        (err) => {
          this.builderService.showUpdateProgress = false
          alert('error uploading file, please contact support')
        }
      )
    })
    reader.readAsDataURL(file)
  }

  /**
   * Removes the uploaded p8 key file from the settings data
   */
  removeFile(filePath: string) {
    if (filePath === 'appleSettingsData.key_filepath') {
      this.appleSettingsData.key_filepath = ''
    }
  }
}
